<template>
    <multiselect 
        :model-value="currentModelValue"
        @update:model-value="handleUpdate($event)"
        ref="selectRef" 
        :options="items"
        :searchable="searchable" 
        :show-labels="false" 
        :show-no-results="false" 
        :disabled="disabled"
        label="label" 
        track-by="value"
        :placeholder="placeholder || ''"
        class="multiSelectContainer"
    >
        <template v-if="hasSearchIcon" #selection="{ values }">
            <div class="multiselect__selection-wrapper">
                <i class="search-icon fa-solid fa-magnifying-glass"></i>
                <template v-if="values?.length">
                    <span
                        class="multiselect__tag"
                        v-for="(option, index) in values"
                        :key="option?.value ?? index"
                    >
                        <span>{{ option?.label ?? option }}</span>
                    </span>
                </template>
            </div>
        </template>
        <template v-if="hasOtherOption" #[camel(`after-list`)]>
            <li class="multiselect__element" role="option">
                <span class="multiselect__option other" @click="selectOther">
                    Other
                </span>
            </li>
        </template>
    </multiselect>
</template>

<script lang="ts" setup>
import { computed, ref, type PropType } from 'vue'
import Multiselect from 'vue-multiselect'
import multiselectCss from 'vue-multiselect/dist/vue-multiselect.min.css?inline'
import selectCss from './Select.styles.css?inline'

// Inject the styles into the document head if they haven't been added yet
if (typeof document !== 'undefined' && !document.getElementById('atlas-select-styles'))
{
    const style = document.createElement('style')
    style.id = 'atlas-select-styles'
    style.textContent = multiselectCss + '\n' + selectCss
    document.head.appendChild(style)
}


interface SelectOption {
    label: string
    value: string | number
}

const props = defineProps({
    modelValue: {
        type: [String, Number, Object, Array] as PropType<string | number | null | SelectOption | SelectOption[]>,
        default: null,
    },
    items: {
        type: Array as PropType<SelectOption[] | Array<{ Text: string; Value: string }>>,
        default: () => [] as SelectOption[],
    },
    options: {
        type: Array as PropType<SelectOption[] | Array<{ Text: string; Value: string }>>,
        default: () => [] as SelectOption[],
    },
    searchable: {
        type: Boolean,
        default: true,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    placeholder: {
        type: String,
        default: '',
    },
    hasSearchIcon: {
        type: Boolean,
        default: false,
    },
    hasOtherOption: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits<{
    'update:modelValue': [value: string | number | null | SelectOption | SelectOption[]]
    'select-other': []
}>()

// Normalize items from either prop name (Vueform) or options prop (legacy)
const items = computed(() => {
    const sourceItems = props.items ?? props.options ?? []
    
    return sourceItems.map(item => {
        // Handle both Vueform format { label, value } and legacy format { Text, Value }
        if ('label' in item && 'value' in item) {
            return { label: item.label, value: item.value }
        } else if ('Text' in item && 'Value' in item) {
            return { label: item.Text, value: item.Value }
        }
        return item as SelectOption
    })
})

const mapToOptionObject = (value: string | number | SelectOption | null | undefined): SelectOption | string | number | null | undefined => {
    const normalizedValue = (value as any)?.value ?? (value as any)?.Value ?? value
    const matchedOption = items.value.find(item => item.value === normalizedValue)
    return matchedOption ?? value
}

const currentModelValue = computed(() => {
    if (Array.isArray(props.modelValue)) {
        return props.modelValue.map(mapToOptionObject)
    }

    if (props.modelValue && typeof props.modelValue === 'object') {
        return mapToOptionObject(props.modelValue)
    }

    return mapToOptionObject(props.modelValue)
})

const selectRef = ref<any>(null)

const handleUpdate = (event: any) => {
    if (Array.isArray(event)) {
        emit('update:modelValue', event.map(item => item?.value ?? item?.Value ?? item))
        return
    }

    emit('update:modelValue', event?.value ?? event?.Value ?? event)
}

const selectOther = () => {
    emit('select-other')
}

const camel = (str: string) => str.replace(/-([a-z])/g, (g) => g[1].toUpperCase())
</script>